import { Flex, Text } from '@mantine/core';
import IconAlertTriangle from '@tabler/icons-react/dist/esm/icons/IconAlertTriangle.mjs';
import { useTranslation } from 'react-i18next';
import {
  formatTQLNodeLabel,
  getTQLNodeFallbackKey,
  TQL_NODE_ID_PREFIX,
  type TQLNodeInfo,
} from '../lib/tql-node';

interface Props {
  node: TQLNodeInfo;
  /** Push the identifier to the right edge — the layout used in data grids. */
  fullWidth?: boolean;
  /** Show the CI type system name; it is what a Jinja reference is built from. */
  withCIType?: boolean;
  /** Hide technical identifiers in summaries such as status propagation. */
  withId?: boolean;
  fz?: number | string;
  'data-testid'?: string;
}

/**
 * The one place that decides how a TQL node looks. Name and identifier stay on a
 * single line. Identifiers are shown by default for addressing the node in
 * `ci_hint` rules and metric targets; summaries can opt out with `withId`.
 */
export function TQLNodeLabel({
  node,
  fullWidth,
  withCIType,
  withId = true,
  fz = 14,
  'data-testid': testId = 'tql-node-label',
}: Props) {
  const { t } = useTranslation();

  const isBroken = node.status !== 'resolved';
  const name = formatTQLNodeLabel(
    node,
    t(getTQLNodeFallbackKey(node), { ns: 'settings' }),
    false,
  );

  // Everything is rendered with spans: the label goes into table cells, select
  // options, tabs and MultiSelect pills, and a pill is a span itself.
  return (
    <Flex
      component='span'
      data-testid={testId}
      gap='xs'
      px={1}
      align='center'
      wrap='nowrap'
      justify={fullWidth ? 'space-between' : undefined}
      classNames={fullWidth ? { root: 'w-full' } : undefined}
    >
      <Flex component='span' gap='xs' align='center' wrap='nowrap' miw={0}>
        {isBroken && <IconAlertTriangle size={22} color='orange' />}
        <Text
          component='span'
          data-testid={`${testId}-name`}
          fz={fz}
          truncate='end'
          c={isBroken ? 'dimmed' : undefined}
          title={name}
        >
          {name}
        </Text>
        {withCIType && node.ciTypeName && (
          <Text
            component='span'
            data-testid={`${testId}-ci-type`}
            fz={fz}
            c='dimmed'
            truncate='end'
          >
            {node.ciTypeName}
          </Text>
        )}
      </Flex>
      {withId && (
        <Text
          component='span'
          data-testid={`${testId}-id`}
          fz={fz}
          c='dimmed'
          className='whitespace-nowrap'
        >
          {`${TQL_NODE_ID_PREFIX} ${node.nodeId}`}
        </Text>
      )}
    </Flex>
  );
}
